/**
 * Handler: remove_from_cart event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct, StorefrontCart } from '../../types/storefront';
import { adaptProduct } from '../../adapters/product';
import { generateEventId, gtmMarketFields } from '../utils';

export function handleRemoveFromCart(
  analyzify: WindowAnalyzify,
  product: StorefrontProduct,
  quantity: number,
  cart: StorefrontCart,
  variantId?: string
): void {
  // The removed line's variant; without it the product's first variant.
  const { product: adaptedProduct, variant } = adaptProduct(product, variantId);
  const eventId = generateEventId();

  // CDN expects a flat cart-item object with product_id, variant_id, price, quantity, etc.
  const cartItem = {
    product_id: adaptedProduct.id,
    variant_id: variant.id,
    price: variant.price,
    quantity: quantity,
    title: adaptedProduct.title,
    product_title: adaptedProduct.title,
    product_type: adaptedProduct.type,
    type: adaptedProduct.type,
    vendor: adaptedProduct.vendor,
    sku: variant.sku,
    variant_title: variant.title,
    handle: adaptedProduct.handle,
  };

  // GA4
  if (typeof analyzify.gaRemoveFromCart === 'function') {
    analyzify.gaRemoveFromCart(cartItem, eventId);
  }

  // GTM - try CDN first, fall back to direct dataLayer push
  const win = window as any;
  let gtmTracked = false;
  if (typeof analyzify.gtmRemoveFromCart === 'function') {
    try {
      analyzify.gtmRemoveFromCart(cartItem, eventId);
      gtmTracked = true;
    } catch {
      // CDN function failed — fall through to dataLayer push
    }
  }

  if (!gtmTracked && Array.isArray(win.dataLayer) && analyzify.properties?.GTM?.status) {
    const itemPrice = analyzify.formatPrice?.(variant.price, true) ?? variant.price / 100;

    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: 'ee_remove_from_cart',
      ss_event_id: eventId || analyzify.eventId.get('remove_from_cart') || null,
      ecommerce: {
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        currency: analyzify.currency || null,
        cart_id: analyzify.cart_id || null,
        value: itemPrice * (quantity || 1),
        items: [{
          id: analyzify.getShopifyId?.(
            analyzify.feed_region || '',
            adaptedProduct.id,
            variant.id,
            variant.sku
          ) || null,
          item_id: adaptedProduct.id?.toString() || null,
          item_name: adaptedProduct.title?.trim() || null,
          price: itemPrice,
          item_category: adaptedProduct.type || null,
          item_brand: adaptedProduct.vendor || null,
          discount: 0,
          item_barcode: variant.barcode || null,
          item_variant: variant.title?.trim() || null,
          item_variant_id: variant.id?.toString() || null,
          item_sku: variant.sku || null,
          index: 0,
          quantity: quantity || 1,
        }],
      },
      ...gtmMarketFields(analyzify),
    });
  }

  // Clarity
  if (typeof analyzify.clarityRemoveFromCart === 'function') {
    analyzify.clarityRemoveFromCart();
  }

  // Hotjar
  if (typeof analyzify.hotjarRemoveFromCart === 'function') {
    analyzify.hotjarRemoveFromCart();
  }
}
